import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Backup,
  Dashboard,
  Storage,
  Speed,
  Home,
  Logout,
  People,
  AdminPanelSettings,
  Assignment,
  Settings,
  Notifications,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import ThemeToggle from '../Theme/ThemeToggle';

const MobileAdminNavigation = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode } = useThemeMode();
  const theme = useTheme();

  // 관리자 메뉴
  const adminMenuItems = [
    { path: '/admin', label: '시스템 대시보드', icon: <Dashboard /> },
    { path: '/admin/database', label: 'DB 통계', icon: <Storage /> },
    { path: '/admin/performance', label: '성능 모니터링', icon: <Speed /> },
    { path: '/admin/users', label: '사용자 관리', icon: <People /> },
    { path: '/admin/task-logs', label: '작업 로그', icon: <Assignment /> },
    { path: '/admin/settings', label: '시스템 설정', icon: <Settings /> },
    { path: '/admin/notifications', label: '공지 관리', icon: <Notifications /> },
    { path: '/admin/backup', label: '백업/복원', icon: <Backup /> },
  ];

  // 기타 메뉴
  const otherMenuItems = [
    { path: '/', label: '메인으로 돌아가기', icon: <Home /> },
    { action: 'logout', label: '로그아웃', icon: <Logout /> },
  ];

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleMenuClick = (item) => {
    if (item.action === 'logout') {
      logout();
    } else {
      navigate(item.path);
    }
    setIsDrawerOpen(false);
  };

  const isCurrentPath = (path) => {
    return location.pathname === path || 
           (path !== '/admin' && location.pathname.startsWith(path));
  };

  const renderMenuItem = (item, isActive = false) => (
    <ListItem key={item.path || item.action} disablePadding>
      <ListItemButton
        onClick={() => handleMenuClick(item)}
        sx={{
          minHeight: 48,
          px: 2,
          py: 1.5,
          backgroundColor: isActive 
            ? alpha(theme.palette.error.main, 0.1)
            : 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.05),
          },
          borderRadius: 1,
          mx: 1,
          my: 0.5,
        }}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: 40,
            color: isActive ? theme.palette.error.main : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText 
          primary={item.label}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '0.95rem',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? theme.palette.error.main : 'inherit',
            },
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 헤더 영역 */}
      <Box
        sx={{
          p: 2,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600} color="error.main">
            Admin Panel
          </Typography>
          <IconButton 
            onClick={handleDrawerToggle}
            size="small"
            sx={{ 
              color: theme.palette.text.secondary, 
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* 관리자 정보 */}
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40,
              backgroundColor: theme.palette.error.main,
            }}
          >
            <AdminPanelSettings />
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.nickname || user?.username || '관리자'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              관리자 모드
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 관리자 메뉴 */}
      <Box sx={{ flex: 1, py: 1 }}>
        <Typography 
          variant="overline" 
          sx={{ 
            px: 2, 
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 600, 
          }}
        >
          관리자 메뉴
        </Typography>
        <List sx={{ px: 0 }}>
          {adminMenuItems.map(item => 
            renderMenuItem(item, isCurrentPath(item.path)),
          )}
        </List>
      </Box>

      {/* 하단 영역 - 테마 토글 및 기타 */}
      <Box
        sx={{
          mt: 'auto',
          borderTop: `1px solid ${theme.palette.divider}`,
          p: 2,
        }}
      >
        {/* 테마 토글 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            다크모드
          </Typography>
          <ThemeToggle />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 기타 메뉴 */}
        <List sx={{ px: 0, py: 0 }}>
          {otherMenuItems.map(item => 
            renderMenuItem(item, item.path ? isCurrentPath(item.path) : false),
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* 햄버거 메뉴 버튼 - 모바일에서만 표시 */}
      <IconButton
        color="inherit"
        aria-label="open admin drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ 
          display: { xs: 'block', sm: 'none' },
          mr: 1,
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* 모바일 관리자 네비게이션 드로어 */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={isDrawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: isDarkMode 
              ? theme.palette.background.paper 
              : theme.palette.background.default,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default MobileAdminNavigation;